// TipTap tabanlı metin editörü (kalın, italik, altı çizili, başlık, liste, renk, bağlantı, hizalama)
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import { Bold, Italic, Underline, Strikethrough, Heading2, Heading3, List, ListOrdered, Link2, Unlink, AlignLeft, AlignCenter, AlignRight, Undo2, Redo2, Eraser } from 'lucide-react';

const COLORS = ['#0f172a', '#dc2626', '#d97706', '#16a34a', '#2563eb', '#7c3aed'];

const Btn = ({ on, onClick, title, children, testId }) => (
  <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick} data-testid={testId}
    className={`h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors ${on ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-200'}`}>{children}</button>
);

const Toolbar = ({ editor }) => {
  const c = () => editor.chain().focus();
  const setLink = () => {
    const prev = editor.getAttributes('link').href || 'https://';
    const url = window.prompt('Bağlantı adresi (https://...)', prev);
    if (url === null) return;
    if (!url.trim()) { c().unsetLink().run(); return; }
    c().extendMarkRange('link').setLink({ href: url.trim(), target: '_blank' }).run();
  };
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b bg-slate-50 px-2 py-1.5" data-testid="rte-toolbar">
      <Btn on={editor.isActive('bold')} onClick={() => c().toggleBold().run()} title="Kalın" testId="rte-bold"><Bold className="h-4 w-4" /></Btn>
      <Btn on={editor.isActive('italic')} onClick={() => c().toggleItalic().run()} title="İtalik" testId="rte-italic"><Italic className="h-4 w-4" /></Btn>
      <Btn on={editor.isActive('underline')} onClick={() => c().toggleUnderline().run()} title="Altı çizili" testId="rte-underline"><Underline className="h-4 w-4" /></Btn>
      <Btn on={editor.isActive('strike')} onClick={() => c().toggleStrike().run()} title="Üstü çizili" testId="rte-strike"><Strikethrough className="h-4 w-4" /></Btn>
      <span className="mx-1 h-5 w-px bg-slate-300" />
      <Btn on={editor.isActive('heading', { level: 2 })} onClick={() => c().toggleHeading({ level: 2 }).run()} title="Büyük başlık" testId="rte-h2"><Heading2 className="h-4 w-4" /></Btn>
      <Btn on={editor.isActive('heading', { level: 3 })} onClick={() => c().toggleHeading({ level: 3 }).run()} title="Küçük başlık" testId="rte-h3"><Heading3 className="h-4 w-4" /></Btn>
      <Btn on={editor.isActive('bulletList')} onClick={() => c().toggleBulletList().run()} title="Madde işaretli liste" testId="rte-ul"><List className="h-4 w-4" /></Btn>
      <Btn on={editor.isActive('orderedList')} onClick={() => c().toggleOrderedList().run()} title="Numaralı liste" testId="rte-ol"><ListOrdered className="h-4 w-4" /></Btn>
      <span className="mx-1 h-5 w-px bg-slate-300" />
      {[['left', AlignLeft, 'Sola'], ['center', AlignCenter, 'Ortaya'], ['right', AlignRight, 'Sağa']].map(([a, I, t]) => (
        <Btn key={a} on={editor.isActive({ textAlign: a })} onClick={() => c().setTextAlign(a).run()} title={`${t} hizala`} testId={`rte-align-${a}`}><I className="h-4 w-4" /></Btn>
      ))}
      <span className="mx-1 h-5 w-px bg-slate-300" />
      {COLORS.map(col => (
        <button key={col} type="button" title="Yazı rengi" onMouseDown={(e) => e.preventDefault()} onClick={() => c().setColor(col).run()} data-testid={`rte-color-${col.slice(1)}`}
          className={`h-5 w-5 rounded-full border-2 transition-transform hover:scale-110 ${editor.isActive('textStyle', { color: col }) ? 'border-slate-900' : 'border-white shadow'}`} style={{ backgroundColor: col }} />
      ))}
      <span className="mx-1 h-5 w-px bg-slate-300" />
      <Btn on={editor.isActive('link')} onClick={setLink} title="Bağlantı ekle" testId="rte-link"><Link2 className="h-4 w-4" /></Btn>
      <Btn onClick={() => c().unsetLink().run()} title="Bağlantıyı kaldır" testId="rte-unlink"><Unlink className="h-4 w-4" /></Btn>
      <Btn onClick={() => c().unsetAllMarks().clearNodes().run()} title="Biçimi temizle" testId="rte-clear"><Eraser className="h-4 w-4" /></Btn>
      <span className="ml-auto" />
      <Btn onClick={() => c().undo().run()} title="Geri al" testId="rte-undo"><Undo2 className="h-4 w-4" /></Btn>
      <Btn onClick={() => c().redo().run()} title="Yinele" testId="rte-redo"><Redo2 className="h-4 w-4" /></Btn>
    </div>
  );
};

export const RichTextEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false, autolink: true } }),
      TextStyle, Color, TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: { attributes: { class: 'announcement-content min-h-[180px] max-h-[360px] overflow-y-auto px-4 py-3 text-sm focus:outline-none', 'data-testid': 'rte-content' } },
  });
  if (!editor) return null;
  return (
    <div className="rounded-lg border bg-white focus-within:ring-2 focus-within:ring-slate-300" data-testid="rich-text-editor">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};
